/**
 * Hero sample C — geometric abstract + floating feature cards.
 *
 * Pure inline SVG. Bold central circle, stylised stick-figure in motion
 * + 3 floating feature cards (AI Scribe, ROM Chart, Compliance) that
 * drift in/out via CSS-keyframe transforms.
 */
import React from 'react';

export default function HeroSampleC() {
  return (
    <>
      <style>{`
        @keyframes drift-1 { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
        @keyframes drift-2 { 0%,100%{transform:translateY(0)} 50%{transform:translateY(10px)} }
        @keyframes drift-3 { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        @keyframes spin-slow { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        .c-drift-1 { animation: drift-1 5s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
        .c-drift-2 { animation: drift-2 5.5s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
        .c-drift-3 { animation: drift-3 4.5s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
        .c-spin    { animation: spin-slow 60s linear infinite; transform-box: fill-box; transform-origin: center; }
      `}</style>
      <svg viewBox="0 0 600 600" className="w-full h-full" data-testid="hero-sample-c" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <radialGradient id="c-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#F2A100" stopOpacity="0.35"/>
            <stop offset="60%" stopColor="#F2A100" stopOpacity="0.07"/>
            <stop offset="100%" stopColor="#F2A100" stopOpacity="0"/>
          </radialGradient>
          <linearGradient id="c-card-1" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#003D80"/>
            <stop offset="100%" stopColor="#1A5AAB"/>
          </linearGradient>
        </defs>

        {/* Big halo */}
        <circle cx="300" cy="310" r="280" fill="url(#c-glow)"/>

        {/* Concentric rotating rings (subtle) */}
        <g className="c-spin">
          <circle cx="300" cy="310" r="180" fill="none" stroke="#F2EBDB" strokeWidth="1" strokeDasharray="2 8" opacity="0.45"/>
          <circle cx="300" cy="310" r="230" fill="none" stroke="#F2EBDB" strokeWidth="1" strokeDasharray="1 12" opacity="0.3"/>
        </g>

        {/* Bold central filled circle (brand) */}
        <circle cx="300" cy="310" r="120" fill="#F2A100"/>
        <circle cx="300" cy="310" r="118" fill="none" stroke="#003D80" strokeWidth="3"/>

        {/* Stick-figure in motion — mid-squat / lunge */}
        <g stroke="#003D80" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" fill="none">
          {/* Head */}
          <circle cx="300" cy="260" r="18" fill="#003D80"/>
          {/* Torso */}
          <line x1="300" y1="278" x2="300" y2="335"/>
          {/* Arms — one up, one out (suggesting movement) */}
          <line x1="300" y1="295" x2="265" y2="270"/>
          <line x1="300" y1="295" x2="345" y2="320"/>
          {/* Legs — lunge stance */}
          <line x1="300" y1="335" x2="270" y2="385"/>
          <line x1="270" y1="385" x2="285" y2="410"/>
          <line x1="300" y1="335" x2="335" y2="390"/>
          <line x1="335" y1="390" x2="330" y2="420"/>
        </g>

        {/* Floating feature card 1 — AI SCRIBE (top-left) */}
        <g className="c-drift-1">
          <rect x="60" y="100" width="160" height="78" rx="14" fill="url(#c-card-1)"/>
          <circle cx="92" cy="139" r="16" fill="#F2A100"/>
          {/* Soundwave */}
          <g stroke="#003D80" strokeWidth="2.5" strokeLinecap="round">
            <line x1="84" y1="139" x2="84" y2="139"/>
            <line x1="88" y1="135" x2="88" y2="143"/>
            <line x1="92" y1="131" x2="92" y2="147"/>
            <line x1="96" y1="135" x2="96" y2="143"/>
            <line x1="100" y1="139" x2="100" y2="139"/>
          </g>
          <text x="120" y="132" fill="#F2EBDB" fontSize="11" fontWeight="700" fontFamily="ui-sans-serif">AI SCRIBE</text>
          <text x="120" y="148" fill="#F2EBDB" fontSize="9" opacity="0.7" fontFamily="ui-sans-serif">live transcript →</text>
          {/* mini waveform bars */}
          {[0,1,2,3,4,5].map(i => (
            <rect key={i} x={120 + i*10} y={158} width="6" height={5 + (i%3)*6} fill="#F2A100" rx="1"/>
          ))}
        </g>

        {/* Floating feature card 2 — ROM CHART (top-right) */}
        <g className="c-drift-2">
          <rect x="400" y="80" width="160" height="100" rx="14" fill="#F2EBDB" stroke="#003D80" strokeWidth="1.5"/>
          <text x="416" y="106" fill="#003D80" fontSize="11" fontWeight="700">ROM CHART</text>
          <line x1="416" y1="112" x2="476" y2="112" stroke="#F2A100" strokeWidth="2"/>
          {/* polyline */}
          <polyline points="416,160 432,150 448,158 464,138 480,144 496,128 512,134 528,118 544,124"
                    fill="none" stroke="#003D80" strokeWidth="2"/>
          <circle cx="544" cy="124" r="4" fill="#F2A100"/>
        </g>

        {/* Floating feature card 3 — GDPR shield (bottom-right) */}
        <g className="c-drift-3">
          <rect x="420" y="430" width="150" height="78" rx="14" fill="#fff" stroke="#003D80" strokeWidth="1.5"/>
          <path d="M450 448 L450 472 Q450 488 466 494 Q482 488 482 472 L482 448 Z"
                fill="#F2A100" stroke="#003D80" strokeWidth="2"/>
          <path d="M460 470 L466 476 L474 462" fill="none" stroke="#003D80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          <text x="498" y="464" fill="#003D80" fontSize="11" fontWeight="700">HIPAA · GDPR</text>
          <text x="498" y="480" fill="#003D80" fontSize="9" opacity="0.7">audit-ready by default</text>
        </g>

        {/* Floating feature card 4 — Calendar (bottom-left) */}
        <g className="c-drift-1" style={{ animationDelay: '1s' }}>
          <rect x="40" y="430" width="140" height="78" rx="14" fill="#003D80"/>
          <rect x="56" y="448" width="44" height="44" rx="6" fill="#F2EBDB"/>
          <text x="78" y="464" textAnchor="middle" fontSize="8" fontWeight="700" fill="#003D80">MAR</text>
          <text x="78" y="486" textAnchor="middle" fontSize="16" fontWeight="800" fill="#003D80">14</text>
          <text x="112" y="464" fill="#F2EBDB" fontSize="10" fontWeight="700">10:30 AM</text>
          <text x="112" y="478" fill="#F2EBDB" fontSize="9" opacity="0.7">Knee assessment</text>
          <text x="112" y="492" fill="#F2EBDB" fontSize="9" opacity="0.7">Eleanor V.</text>
        </g>

        {/* Brand wordmark */}
        <text x="300" y="555" textAnchor="middle" fontSize="14" fontWeight="800"
              fontFamily="ui-sans-serif" fill="#F2EBDB" letterSpacing="3">PHYSIOGENIC</text>
      </svg>
    </>
  );
}
